// Trend chart — line dual-axis (receita esquerda, sessões orgânicas direita).
// Markers verticais opcionais (datas de tarefas concluídas).
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";
import { fmtBRL, fmtNum, fmtDateShort } from "../../lib/format.ts";
import { chartTheme, tooltipStyle, defaultMargin } from "./theme.ts";

interface DataPoint {
  date: string;
  revenue: number;
  organic_sessions: number;
  orders?: number;
}

interface Marker { date: string; title: string }

interface Props {
  data: DataPoint[];
  markers?: Marker[];
  height?: number;
}

export function TrendChart({ data, markers = [], height = 280 }: Props) {
  if (!data.length) return <div className="center-loading">sem dados ainda</div>;
  const t = chartTheme();

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ ...defaultMargin, right: 48 }}>
        <CartesianGrid stroke={t.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={(d) => fmtDateShort(d)}
          tick={{ fontSize: 10, fill: t.muted, fontFamily: "JetBrains Mono, monospace" }}
          axisLine={{ stroke: t.grid }}
          tickLine={{ stroke: t.grid }}
          minTickGap={40}
        />
        <YAxis
          yAxisId="left"
          tickFormatter={(n) => fmtBRL(n)}
          tick={{ fontSize: 10, fill: t.muted, fontFamily: "JetBrains Mono, monospace" }}
          axisLine={false}
          tickLine={false}
          width={70}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          tickFormatter={(n) => fmtNum(n)}
          tick={{ fontSize: 10, fill: t.muted, fontFamily: "JetBrains Mono, monospace" }}
          axisLine={false}
          tickLine={false}
          width={56}
        />
        <Tooltip
          {...tooltipStyle}
          labelFormatter={(d) => fmtDateShort(d as string)}
          formatter={(value, name) => {
            if (name === "Receita") return [fmtBRL(value as number), name];
            if (name === "Sessões orgânicas") return [fmtNum(value as number), name];
            return [value, name];
          }}
        />
        <Legend
          wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
          iconType="rect"
        />
        {markers.map((m, i) => {
          // Só renderiza marker se a data existe nos dados
          if (!data.find((d) => d.date === m.date)) return null;
          return (
            <ReferenceLine
              key={`mk-${i}`}
              x={m.date}
              yAxisId="left"
              stroke={t.green}
              strokeDasharray="3 3"
              strokeOpacity={0.6}
              label={{ value: "✓", position: "top", fill: t.green, fontSize: 12, fontWeight: 700 }}
            />
          );
        })}
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="revenue"
          name="Receita"
          stroke={t.primary}
          strokeWidth={2.2}
          dot={false}
          activeDot={{ r: 4, fill: t.primary }}
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="organic_sessions"
          name="Sessões orgânicas"
          stroke={t.accent}
          strokeWidth={2.2}
          dot={false}
          activeDot={{ r: 4, fill: t.accent }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
